import React, { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Medal, Store, TrendingUp, Award, ShoppingBag, Gift, DollarSign, GraduationCap, ChevronLeft, ChevronRight, Ticket, Calendar, Loader2, MapPin, Edit2, Save, Trash2, FileText, Zap, Lock, RefreshCw, Crown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AchievementBadge } from './achievements/AchievementBadge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { MemberAchievementsView } from './achievements/MemberAchievementsView';
import { toast } from 'sonner';
import { useSystemAchievementOverrides } from '@/hooks/useSystemAchievementOverrides';

// Membership sales (Oro/Plata/Bronce)
interface MembershipSale {
  id: string;
  plan: string;
  billing_period: string;
  activated_at: string;
  member_name: string;
  member_phone: string;
}

// Discount purchases (Canjes de productos)
interface DiscountPurchase {
  id: string;
  product_name: string;
  category: string;
  original_price: number;
  final_price: number;
  savings: number;
  quantity: number;
  created_at: string;
  customer_name?: string;
}

interface MemberRedemption {
  id: string;
  benefit_type: string;
  quantity: number;
  created_at: string;
  notes: string | null;
  customer_name?: string;
}

interface MemberStats {
  totalMembershipSales: number;
  totalDiscountPurchases: number;
  totalAmount: number;
  totalSavings: number;
  totalRedemptions: number;
}

interface SelectedSeller {
  user_id: string;
  nombre: string;
  ubicacion_tienda: string | null;
}
interface SellerAchievement {
  seller_id: string;
  achievement_type: string;
  unlocked_at: string;
}

interface SellerStats {
  user_id: string;
  nombre: string;
  ubicacion_tienda: string | null;
  total_ventas: number;
  total_canjes: number;
  total_ahorros: number;
  cursos_completados: number;
  total_cursos: number;
  training_completed: boolean;
  achievement_count: number;
  achievement_points: number;
  achievements: SellerAchievement[];
  // New score breakdown fields from RPC
  bronce_sales: number;
  plata_sales: number;
  oro_sales: number;
  total_score: number;
  membership_sale_points: number;
  redemption_points: number;
  passive_redemption_points: number;
  savings_points: number;
  cycle_milestone_points: number;
}

interface SellerRankingDashboardProps {
  className?: string;
}

const PODIUM_COLORS = {
  1: { bg: 'bg-gradient-to-br from-yellow-500/30 to-amber-600/20', border: 'border-yellow-500/50', text: 'text-yellow-400', icon: 'text-yellow-400' },
  2: { bg: 'bg-gradient-to-br from-slate-400/30 to-slate-500/20', border: 'border-slate-400/50', text: 'text-slate-300', icon: 'text-slate-300' },
  3: { bg: 'bg-gradient-to-br from-amber-700/30 to-orange-800/20', border: 'border-amber-600/50', text: 'text-amber-500', icon: 'text-amber-500' },
};

export const SellerRankingDashboard: React.FC<SellerRankingDashboardProps> = ({ className }) => {
  const { user, isAdmin, isVendedor } = useAuth();
  const { getAchievementPoints, isAchievementActive, loading: overridesLoading } = useSystemAchievementOverrides();
  const [sellers, setSellers] = useState<SellerStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<'week' | 'month' | 'all'>('month');
  const [allAchievements, setAllAchievements] = useState<SellerAchievement[]>([]);
  const [syncing, setSyncing] = useState(false);
  
  // Selected seller state for detail panel
  const [selectedSeller, setSelectedSeller] = useState<SelectedSeller | null>(null);
  const [detailsTab, setDetailsTab] = useState('ventas');
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [memberStats, setMemberStats] = useState<MemberStats | null>(null);
  const [membershipSales, setMembershipSales] = useState<MembershipSale[]>([]);
  const [discountPurchases, setDiscountPurchases] = useState<DiscountPurchase[]>([]);
  const [memberRedemptions, setMemberRedemptions] = useState<MemberRedemption[]>([]);

  // Format currency helper
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: 'CRC',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount).replace('CRC', '₡');
  };

  // Get ranking for selected seller
  const getSellerRank = (sellerId: string) => {
    const index = sellers.findIndex(s => s.user_id === sellerId);
    return index >= 0 ? index + 1 : null;
  };

  // Fetch member details when selected
  useEffect(() => {
    if (selectedSeller) {
      fetchMemberDetails(selectedSeller.user_id);
    }
  }, [selectedSeller]);

  const fetchMemberDetails = async (userId: string) => {
    setLoadingDetails(true);
    try {
      // 1. Fetch MEMBERSHIP SALES (Oro/Plata/Bronce) sold by this seller
      const { data: membershipData } = await supabase
        .from('memberships')
        .select('id, plan, billing_period, activated_at, user_id')
        .eq('sold_by', userId)
        .not('activated_at', 'is', null)
        .order('activated_at', { ascending: false })
        .limit(50);

      if (membershipData && membershipData.length > 0) {
        const memberIds = [...new Set(membershipData.map(m => m.user_id))];
        const { data: members } = await supabase
          .from('profiles')
          .select('user_id, nombre, telefono')
          .in('user_id', memberIds);

        const memberMap = new Map((members || []).map(m => [m.user_id, { nombre: m.nombre, telefono: m.telefono }]));
        
        setMembershipSales(membershipData.map(m => ({
          id: m.id,
          plan: m.plan,
          billing_period: m.billing_period,
          activated_at: m.activated_at!,
          member_name: memberMap.get(m.user_id)?.nombre || 'Cliente',
          member_phone: memberMap.get(m.user_id)?.telefono || ''
        })));
      } else {
        setMembershipSales([]);
      }

      // 2. Fetch DISCOUNT PURCHASES (canjes de productos) by this seller
      const { data: purchases } = await supabase
        .from('discount_purchases')
        .select('id, product_name, category, original_price, final_price, savings, quantity, created_at, user_id')
        .eq('registered_by', userId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (purchases && purchases.length > 0) {
        const customerIds = [...new Set(purchases.map(s => s.user_id))];
        const { data: customers } = await supabase
          .from('profiles')
          .select('user_id, nombre')
          .in('user_id', customerIds);

        const customerMap = new Map(customers?.map(c => [c.user_id, c.nombre]) || []);
        
        setDiscountPurchases(purchases.map(s => ({
          ...s,
          customer_name: customerMap.get(s.user_id) || 'Cliente'
        })));
      } else {
        setDiscountPurchases([]);
      }

      // 3. Fetch benefit redemptions by this seller
      const { data: redemptions } = await supabase
        .from('benefit_redemptions')
        .select('id, benefit_type, quantity, created_at, notes, membership_id')
        .eq('redeemed_by', userId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (redemptions && redemptions.length > 0) {
        const membershipIds = [...new Set(redemptions.map(r => r.membership_id))];
        const { data: memberships } = await supabase
          .from('memberships')
          .select('id, user_id')
          .in('id', membershipIds);

        const userIds = memberships?.map(m => m.user_id) || [];
        const { data: customers } = await supabase
          .from('profiles')
          .select('user_id, nombre')
          .in('user_id', userIds);

        const membershipToUser = new Map(memberships?.map(m => [m.id, m.user_id]) || []);
        const userToName = new Map(customers?.map(c => [c.user_id, c.nombre]) || []);

        setMemberRedemptions(redemptions.map(r => ({
          ...r,
          customer_name: userToName.get(membershipToUser.get(r.membership_id) || '') || 'Cliente'
        })));
      } else {
        setMemberRedemptions([]);
      }

      // Calculate stats
      const totalMembershipSales = membershipData?.length || 0;
      const totalDiscountPurchases = purchases?.length || 0;
      const totalAmount = purchases?.reduce((sum, s) => sum + s.final_price, 0) || 0;
      const totalSavings = purchases?.reduce((sum, s) => sum + s.savings, 0) || 0;
      const totalRedemptions = redemptions?.reduce((sum, r) => sum + r.quantity, 0) || 0;

      setMemberStats({
        totalMembershipSales,
        totalDiscountPurchases,
        totalAmount,
        totalSavings,
        totalRedemptions
      });
    } catch (error) {
      console.error('Error fetching member details:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleSelectSeller = (seller: SellerStats) => {
    setSelectedSeller({
      user_id: seller.user_id,
      nombre: seller.nombre,
      ubicacion_tienda: seller.ubicacion_tienda
    });
    setDetailsTab('ventas');
  };

  const handleBackToList = () => {
    setSelectedSeller(null);
    setMemberStats(null);
    setMembershipSales([]);
    setDiscountPurchases([]);
    setMemberRedemptions([]);
  };

  useEffect(() => {
    if (!overridesLoading) {
      fetchSellerStats();
    }
  }, [period, overridesLoading]);

  const fetchSellerStats = async () => {
    setLoading(true);
    try {
      // Use the RPC that calculates all scores correctly
      const { data: rankingData, error } = await supabase.rpc('get_seller_ranking_stats');
      
      if (error || !rankingData) {
        console.error('Error fetching ranking stats:', error);
        setSellers([]);
        setLoading(false);
        return;
      }

      // Fetch achievements for display
      const sellerIds = rankingData.map((s: any) => s.seller_id);
      const { data: achievementsData } = await supabase
        .from('seller_achievements')
        .select('seller_id, achievement_type, unlocked_at')
        .in('seller_id', sellerIds);

      setAllAchievements(achievementsData || []);

      // Create achievement list map
      const achievementMap: Record<string, SellerAchievement[]> = {};
      (achievementsData || []).forEach(a => {
        if (!achievementMap[a.seller_id]) {
          achievementMap[a.seller_id] = [];
        }
        achievementMap[a.seller_id].push(a);
      });

      // Map RPC data to component format
      const formattedSellers: SellerStats[] = rankingData.map((s: any) => ({
        user_id: s.seller_id,
        nombre: s.seller_name || 'Vendedor',
        ubicacion_tienda: s.ubicacion_tienda,
        total_ventas: Number(s.bronce_sales || 0) + Number(s.plata_sales || 0) + Number(s.oro_sales || 0),
        total_canjes: Number(s.total_canjes) || 0,
        total_ahorros: Number(s.total_ahorros) || 0,
        cursos_completados: Number(s.cursos_completados) || 0,
        total_cursos: Number(s.total_cursos) || 0,
        training_completed: s.training_completed || false,
        achievement_count: Number(s.achievement_count) || 0,
        achievement_points: Number(s.achievement_points) || 0,
        achievements: achievementMap[s.seller_id] || [],
        // New score breakdown fields
        bronce_sales: Number(s.bronce_sales) || 0,
        plata_sales: Number(s.plata_sales) || 0,
        oro_sales: Number(s.oro_sales) || 0,
        total_score: Number(s.total_score) || 0,
        membership_sale_points: Number(s.membership_sale_points) || 0,
        redemption_points: Number(s.redemption_points) || 0,
        passive_redemption_points: Number(s.passive_redemption_points) || 0,
        savings_points: Number(s.savings_points) || 0,
        cycle_milestone_points: Number(s.cycle_milestone_points) || 0,
      }));

      // Already sorted by total_score from RPC
      setSellers(formattedSellers);
    } catch (err) {
      console.error('Error fetching seller stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const top3 = sellers.slice(0, 3);
  const rest = sellers.slice(3);

  const periodLabels = {
    week: 'Esta semana',
    month: 'Este mes',
    all: 'Todo el tiempo',
  };

  // Find current seller's position
  const myRank = user ? sellers.findIndex(s => s.user_id === user.id) + 1 : 0;
  const myData = user ? sellers.find(s => s.user_id === user.id) : null;
  
  // Check if user is a seller (not admin) - show restricted view
  const showRestrictedView = isVendedor && !isAdmin;

  // Calculate month timeline data
  const monthTimelineData = useMemo(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const totalDays = lastDay.getDate();
    const currentDay = now.getDate();
    const daysRemaining = totalDays - currentDay;
    const progress = (currentDay / totalDays) * 100;
    const isLastWeek = daysRemaining <= 7;
    
    return { totalDays, currentDay, daysRemaining, progress, isLastWeek };
  }, []);

  const daysRemaining = monthTimelineData.daysRemaining;

  // Store statistics
  const storeStats = useMemo(() => {
    const stores: Record<string, { ventas: number; canjes: number; ahorros: number }> = {};
    
    sellers.forEach(s => {
      const store = s.ubicacion_tienda || 'Sin asignar';
      if (!stores[store]) {
        stores[store] = { ventas: 0, canjes: 0, ahorros: 0 };
      }
      stores[store].ventas += s.total_ventas;
      stores[store].canjes += s.total_canjes;
      stores[store].ahorros += s.total_ahorros;
    });

    return Object.entries(stores)
      .map(([name, stats]) => ({ name, ...stats }))
      .sort((a, b) => b.ventas - a.ventas);
  }, [sellers]);
  
  // Handle seller clicking their own card in restricted view
  const handleViewOwnDetails = () => {
    if (myData) {
      handleSelectSeller(myData);
    }
  };

  // Sync all achievements - remove orphaned achievements that don't meet requirements
  const syncAllAchievements = async () => {
    if (!isAdmin) return;
    
    setSyncing(true);
    try {
      const currentMonth = format(new Date(), 'yyyy-MM');
      let revokedCount = 0;
      
      // Get all seller IDs
      const { data: sellerRoles } = await supabase
        .from('user_roles')
        .select('user_id')
        .eq('role', 'vendedor');
      
      if (!sellerRoles) {
        toast.error('No se encontraron vendedores');
        return;
      }
      
      // For each seller, check their achievements
      for (const seller of sellerRoles) {
        const sellerId = seller.user_id;
        
        // Get seller's stats
        const { data: allPurchases } = await supabase
          .from('discount_purchases')
          .select('id, savings, month_year')
          .eq('registered_by', sellerId);
        
        const totalVentas = allPurchases?.length || 0;
        const ventasMes = allPurchases?.filter(p => p.month_year === currentMonth).length || 0;
        const totalAhorros = allPurchases?.reduce((sum, p) => sum + (p.savings || 0), 0) || 0;
        
        const { data: allRedemptions } = await supabase
          .from('benefit_redemptions')
          .select('id, quantity')
          .eq('redeemed_by', sellerId);
        
        const totalCanjes = allRedemptions?.reduce((sum, r) => sum + (r.quantity || 1), 0) || 0;
        
        const { data: trainingData } = await supabase
          .from('training_assignments')
          .select('status')
          .eq('seller_id', sellerId);
        
        const trainingCompleted = trainingData && trainingData.length > 0 
          ? trainingData.every(t => t.status === 'completed')
          : false;
        
        // Get this seller's achievements
        const { data: achievements } = await supabase
          .from('seller_achievements')
          .select('id, achievement_type')
          .eq('seller_id', sellerId);
        
        // Check if seller has official prizes (for podium/first_place)
        const { data: prizes } = await supabase
          .from('monthly_prizes')
          .select('id')
          .eq('winner_id', sellerId)
          .in('status', ['claimable', 'claimed']);
        
        const hasOfficialPrize = prizes && prizes.length > 0;
        
        const toRevoke: string[] = [];
        
        for (const achievement of achievements || []) {
          const type = achievement.achievement_type;
          let shouldRevoke = false;
          
          switch (type) {
            case 'first_sale':
              shouldRevoke = totalVentas < 1;
              break;
            case 'sales_10':
              shouldRevoke = ventasMes < 10;
              break;
            case 'sales_50':
              shouldRevoke = totalVentas < 50;
              break;
            case 'sales_100':
              shouldRevoke = totalVentas < 100;
              break;
            case 'redemptions_50':
              shouldRevoke = totalCanjes < 50;
              break;
            case 'savings_100k':
              shouldRevoke = totalAhorros < 100000;
              break;
            case 'savings_500k':
              shouldRevoke = totalAhorros < 500000;
              break;
            case 'podium':
            case 'first_place':
              // Only keep if there's an official prize
              shouldRevoke = !hasOfficialPrize;
              break;
            case 'training_complete':
              shouldRevoke = !trainingCompleted;
              break;
          }
          
          if (shouldRevoke) {
            toRevoke.push(achievement.id);
          }
        }
        
        // Delete revoked achievements
        for (const id of toRevoke) {
          await supabase
            .from('seller_achievements')
            .delete()
            .eq('id', id);
          revokedCount++;
        }
      }
      
      // Refresh the stats
      await fetchSellerStats();
      
      if (revokedCount > 0) {
        toast.success(`Sincronización completa: ${revokedCount} logro(s) revocado(s)`);
      } else {
        toast.success('Sincronización completa: todos los logros están correctos');
      }
    } catch (err) {
      console.error('Error syncing achievements:', err);
      toast.error('Error al sincronizar logros');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 mt-6 flex items-center justify-center min-h-[300px]">
        <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (sellers.length === 0) {
    return (
      <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
        <div className="flex items-center gap-2 mb-4">
          <Trophy className="w-5 h-5 text-yellow-500" />
          <h2 className="text-lg font-bold text-white">Ranking Vendedores</h2>
        </div>
        <p className="text-white/60 text-center py-8">No hay vendedores registrados</p>
      </div>
    );
  }

  // Render seller details panel
  const renderSellerDetails = () => {
    if (!selectedSeller) return null;

    const sellerData = sellers.find(s => s.user_id === selectedSeller.user_id);
    const rank = getSellerRank(selectedSeller.user_id);
    
    const getRankStyles = (r: number) => {
      if (r === 1) return { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30' };
      if (r === 2) return { bg: 'bg-slate-400/20', text: 'text-slate-300', border: 'border-slate-400/30' };
      if (r === 3) return { bg: 'bg-amber-600/20', text: 'text-amber-500', border: 'border-amber-600/30' };
      return { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' };
    };

    const rankStyles = rank ? getRankStyles(rank) : getRankStyles(999);

    return (
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className="space-y-4"
      >
        {/* Back button */}
        <button
          onClick={handleBackToList}
          className="flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-2"
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="text-sm">Volver al ranking</span>
        </button>

        {/* Seller header */}
        <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-4 sm:p-6 border border-white/10">
          <div className="flex items-start gap-3">
            {/* Ranking badge */}
            {rank && (
              <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl ${rankStyles.bg} border ${rankStyles.border} flex flex-col items-center justify-center`}>
                {rank <= 3 && <Trophy className={`w-4 h-4 sm:w-5 sm:h-5 ${rankStyles.text}`} />}
                <span className={`font-bold text-sm sm:text-base ${rankStyles.text}`}>#{rank}</span>
              </div>
            )}
            <div className="flex-1">
              <h2 className="text-white font-semibold text-lg sm:text-xl">{selectedSeller.nombre}</h2>
              <div className="flex items-center gap-2 mt-1">
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400 border border-purple-500/30">
                  Vendedor
                </span>
                {selectedSeller.ubicacion_tienda && (
                  <span className="flex items-center gap-1 text-white/50 text-xs">
                    <MapPin className="w-3 h-3" />
                    {selectedSeller.ubicacion_tienda}
                  </span>
                )}
                {sellerData?.training_completed && (
                  <span className="ml-1 inline-block" title="Entrenamiento completo">🎓</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Stats cards */}
        {loadingDetails ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
          </div>
        ) : memberStats && sellerData && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {/* Score Total Card - Destacado */}
            <div className="bg-gradient-to-br from-amber-500/20 to-yellow-500/10 rounded-xl p-3 border border-amber-500/30 col-span-2 sm:col-span-1 sm:row-span-2">
              <div className="flex items-center gap-2 mb-1">
                <Zap className="w-4 h-4 text-amber-400" />
                <span className="text-white/50 text-xs">Score</span>
              </div>
              <p className="text-amber-400 font-bold text-2xl">
                {sellerData.total_score}
              </p>
              <p className="text-white/40 text-[9px] mt-1">puntos</p>
              <div className="mt-2 space-y-0.5 text-[9px]">
                <div className="flex justify-between text-white/50">
                  <span>Membresías</span>
                  <span className="text-blue-400">+{sellerData.membership_sale_points}</span>
                </div>
                <div className="flex justify-between text-white/50">
                  <span>Canjes</span>
                  <span className="text-pink-400">+{sellerData.redemption_points}</span>
                </div>
                <div className="flex justify-between text-white/50">
                  <span>Pasivos</span>
                  <span className="text-cyan-400">+{sellerData.passive_redemption_points}</span>
                </div>
                <div className="flex justify-between text-white/50">
                  <span>Ahorros</span>
                  <span className="text-green-400">+{sellerData.savings_points}</span>
                </div>
                <div className="flex justify-between text-white/50">
                  <span>Hitos</span>
                  <span className="text-orange-400">+{sellerData.cycle_milestone_points}</span>
                </div>
                <div className="flex justify-between text-white/50">
                  <span>Logros</span>
                  <span className="text-purple-400">+{sellerData.achievement_points}</span>
                </div>
              </div>
            </div>
            {/* Membresías vendidas */}
            <div className="bg-white/5 rounded-xl p-3 border border-white/10">
              <div className="flex items-center gap-2 mb-1">
                <Crown className="w-4 h-4 text-amber-400" />
                <span className="text-white/50 text-xs">Membresías</span>
              </div>
              <p className="text-white font-semibold text-lg">{sellerData.bronce_sales + sellerData.plata_sales + sellerData.oro_sales}</p>
              <div className="text-[10px] text-white/40 space-x-1">
                <span className="text-amber-600">B:{sellerData.bronce_sales}</span>
                <span className="text-slate-400">P:{sellerData.plata_sales}</span>
                <span className="text-yellow-400">O:{sellerData.oro_sales}</span>
              </div>
            </div>
            {/* Canjes de productos */}
            <div className="bg-white/5 rounded-xl p-3 border border-white/10">
              <div className="flex items-center gap-2 mb-1">
                <ShoppingBag className="w-4 h-4 text-blue-400" />
                <span className="text-white/50 text-xs">Canjes</span>
              </div>
              <p className="text-white font-semibold text-lg">{memberStats.totalDiscountPurchases}</p>
            </div>
            <div className="bg-white/5 rounded-xl p-3 border border-white/10">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="w-4 h-4 text-green-400" />
                <span className="text-white/50 text-xs">Monto</span>
              </div>
              <p className="text-white font-semibold text-lg">{formatCurrency(memberStats.totalAmount)}</p>
            </div>
            <div className="bg-white/5 rounded-xl p-3 border border-white/10">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-4 h-4 text-emerald-400" />
                <span className="text-white/50 text-xs">Ahorro</span>
              </div>
              <p className="text-white font-semibold text-lg">{formatCurrency(memberStats.totalSavings)}</p>
            </div>
          </div>
        )}

        {/* Tabs for membership sales / discount purchases / achievements */}
        <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-4 sm:p-6 border border-white/10">
          <Tabs value={detailsTab} onValueChange={setDetailsTab}>
            <TabsList className="bg-white/10 border-white/10 mb-4 w-full">
              <TabsTrigger value="ventas" className="flex-1 data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400">
                <Crown className="w-4 h-4 mr-1.5" />
                Ventas
              </TabsTrigger>
              <TabsTrigger value="canjes" className="flex-1 data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400">
                <ShoppingBag className="w-4 h-4 mr-1.5" />
                Canjes
              </TabsTrigger>
              <TabsTrigger value="logros" className="flex-1 data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-400">
                <Award className="w-4 h-4 mr-1.5" />
                Logros
              </TabsTrigger>
            </TabsList>

            {/* Tab Ventas = Membership Sales (Oro/Plata/Bronce) */}
            <TabsContent value="ventas" className="mt-0">
              {loadingDetails ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-amber-400 animate-spin" />
                </div>
              ) : membershipSales.length === 0 ? (
                <div className="text-center py-8">
                  <Crown className="w-12 h-12 text-white/20 mx-auto mb-3" />
                  <p className="text-white/50 text-sm">No hay membresías vendidas</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {membershipSales.map((sale) => {
                    const getPlanBadgeStyles = (plan: string) => {
                      switch (plan.toLowerCase()) {
                        case 'oro': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
                        case 'plata': return 'bg-slate-400/20 text-slate-300 border-slate-400/30';
                        case 'bronce': return 'bg-amber-600/20 text-amber-500 border-amber-600/30';
                        default: return 'bg-white/10 text-white/60 border-white/20';
                      }
                    };
                    
                    return (
                      <div key={sale.id} className="bg-white/5 rounded-lg p-3 border border-white/10">
                        <div className="flex items-start justify-between">
                          <div className="min-w-0 flex-1">
                            <p className="text-white font-medium text-sm truncate">{sale.member_name}</p>
                            <p className="text-white/50 text-xs">{sale.member_phone}</p>
                          </div>
                          <div className="text-right">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getPlanBadgeStyles(sale.plan)}`}>
                              {sale.plan.toUpperCase()}
                            </span>
                            <p className="text-white/50 text-xs mt-1 capitalize">{sale.billing_period}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-2 text-white/40 text-xs">
                          <Calendar className="w-3 h-3" />
                          {format(new Date(sale.activated_at), 'dd/MM/yyyy HH:mm', { locale: es })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            {/* Tab Canjes = Discount Purchases (productos con descuento) */}
            <TabsContent value="canjes" className="mt-0">
              {loadingDetails ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
                </div>
              ) : discountPurchases.length === 0 ? (
                <div className="text-center py-8">
                  <ShoppingBag className="w-12 h-12 text-white/20 mx-auto mb-3" />
                  <p className="text-white/50 text-sm">No hay canjes de productos</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {discountPurchases.map((purchase) => (
                    <div key={purchase.id} className="bg-white/5 rounded-lg p-3 border border-white/10">
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <p className="text-white font-medium text-sm truncate">{purchase.product_name}</p>
                          <p className="text-white/50 text-xs">{purchase.customer_name}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-green-400 font-medium text-sm">{formatCurrency(purchase.final_price)}</p>
                          <p className="text-emerald-400/70 text-xs">+{formatCurrency(purchase.savings)} ahorro</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-2 text-white/40 text-xs">
                        <Calendar className="w-3 h-3" />
                        {format(new Date(purchase.created_at), 'dd/MM/yyyy HH:mm', { locale: es })}
                        <span className="text-white/20">·</span>
                        <span className="px-1.5 py-0.5 rounded bg-white/10">{purchase.category}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Tab Logros */}
            <TabsContent value="logros" className="mt-0">
              <MemberAchievementsView sellerId={selectedSeller.user_id} />
            </TabsContent>
          </Tabs>
        </div>
      </motion.div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 ${className || ''}`}
    >
      <AnimatePresence mode="wait">
        {selectedSeller ? (
          <motion.div key="details">
            {renderSellerDetails()}
          </motion.div>
        ) : (
          <motion.div
            key="list"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
              <div className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-yellow-500" />
                <h2 className="text-lg font-bold text-white">Ranking Vendedores</h2>
              </div>
              <div className="flex items-center gap-2">
                {isAdmin && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={syncAllAchievements}
                          disabled={syncing}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-orange-400 bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30 rounded-lg transition-colors disabled:opacity-50"
                        >
                          <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} />
                          {syncing ? 'Sincronizando...' : 'Sincronizar'}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Revoca logros que ya no cumplen requisitos</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            </div>

            {/* Month Timeline */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-xl p-4 mb-6"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-amber-400" />
                  <span className="text-white/70 text-sm">Progreso del Mes</span>
                </div>
                <span className={`text-sm font-medium ${monthTimelineData.isLastWeek ? 'text-amber-400 animate-pulse' : 'text-white/60'}`}>
                  {monthTimelineData.isLastWeek && '⚡ '}
                  {daysRemaining} {daysRemaining === 1 ? 'día' : 'días'} restantes
                </span>
              </div>
              
              {/* Timeline bar */}
              <div className="relative h-3 bg-white/10 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${monthTimelineData.progress}%` }}
                  transition={{ duration: 1, ease: "easeOut" }}
                  className="absolute h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full"
                />
                {/* Current day marker */}
                <motion.div
                  initial={{ left: 0 }}
                  animate={{ left: `${monthTimelineData.progress}%` }}
                  transition={{ duration: 1, ease: "easeOut" }}
                  className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-4 h-4 bg-white rounded-full border-2 border-amber-500 shadow-lg shadow-amber-500/50"
                />
              </div>
              
              {/* Labels */}
              <div className="flex justify-between mt-2 text-xs text-white/50">
                <span>Día 1</span>
                <span className="text-amber-400 font-medium">Hoy: Día {monthTimelineData.currentDay}</span>
                <span>Día {monthTimelineData.totalDays}</span>
              </div>
            </motion.div>

            {/* Top 3 Podium - Restricted view for sellers */}
            {showRestrictedView ? (
              <>
                {/* My position card - prominent for sellers - CLICKABLE */}
                {myRank > 0 && myData && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    onClick={handleViewOwnDetails}
                    className="w-full bg-zinc-800/60 rounded-xl p-4 border border-white/10 mb-6 hover:bg-zinc-700/60 hover:scale-[1.01] transition-all cursor-pointer text-left"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl flex flex-col items-center justify-center ${
                        myRank === 1 ? 'bg-yellow-500/20 border border-yellow-500/30' :
                        myRank === 2 ? 'bg-slate-400/20 border border-slate-400/30' :
                        myRank === 3 ? 'bg-amber-600/20 border border-amber-600/30' :
                        'bg-white/5 border border-white/10'
                      }`}>
                        {myRank <= 3 && <Trophy className={`w-4 h-4 ${
                          myRank === 1 ? 'text-yellow-400' :
                          myRank === 2 ? 'text-slate-300' :
                          'text-amber-500'
                        }`} />}
                        <span className={`font-bold text-lg ${
                          myRank === 1 ? 'text-yellow-400' :
                          myRank === 2 ? 'text-slate-300' :
                          myRank === 3 ? 'text-amber-500' :
                          'text-white'
                        }`}>#{myRank}</span>
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-semibold">
                          Posición <span className={`${
                            myRank === 1 ? 'text-yellow-400' :
                            myRank === 2 ? 'text-slate-300' :
                            myRank === 3 ? 'text-amber-500' :
                            'text-amber-400'
                          }`}>#{myRank}</span> de {sellers.length}
                        </p>
                        <div className="flex items-center gap-1 mt-1">
                          <Zap className="w-3 h-3 text-amber-400" />
                          <span className="text-amber-400 font-bold text-sm">{myData.total_score} pts</span>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-white/30" />
                    </div>
                  </motion.button>
                )}

                {/* Podium for sellers - shows names but NOT clickable */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                  {/* Second place */}
                  {top3[1] ? (
                    <div className={`${PODIUM_COLORS[2].bg} border ${PODIUM_COLORS[2].border} rounded-xl p-4 text-center mt-6`}>
                      <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-slate-400/20 mb-2">
                        <Medal className={`w-5 h-5 ${PODIUM_COLORS[2].icon}`} />
                      </div>
                      <p className={`font-bold text-lg ${PODIUM_COLORS[2].text}`}>2°</p>
                      <p className="text-white font-medium text-sm truncate mt-1">
                        {top3[1].user_id === user?.id ? (
                          <span className="text-green-400">¡Tú!</span>
                        ) : (
                          top3[1].nombre.split(' ')[0]
                        )}
                        {top3[1].training_completed && <span className="ml-1">🎓</span>}
                      </p>
                      <p className="text-white/40 text-xs truncate">{top3[1].ubicacion_tienda || ''}</p>
                      <div className="mt-2 flex justify-center gap-2 text-[10px]">
                        <span className="text-blue-400">{top3[1].total_ventas} V</span>
                        <span className="text-purple-400">{top3[1].total_canjes} C</span>
                      </div>
                    </div>
                  ) : <div />}

                  {/* First place */}
                  {top3[0] && (
                    <div className={`${PODIUM_COLORS[1].bg} border ${PODIUM_COLORS[1].border} rounded-xl p-4 text-center relative`}>
                      <motion.div
                        animate={{ scale: [1, 1.1, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className="absolute -top-3 left-1/2 -translate-x-1/2"
                      >
                        <Award className="w-6 h-6 text-yellow-400" />
                      </motion.div>
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-yellow-500/20 mt-2 mb-2">
                        <Trophy className={`w-6 h-6 ${PODIUM_COLORS[1].icon}`} />
                      </div>
                      <p className={`font-bold text-xl ${PODIUM_COLORS[1].text}`}>1°</p>
                      <p className="text-white font-semibold text-sm truncate mt-1">
                        {top3[0].user_id === user?.id ? (
                          <span className="text-green-400">¡Tú!</span>
                        ) : (
                          top3[0].nombre.split(' ')[0]
                        )}
                        {top3[0].training_completed && <span className="ml-1">🎓</span>}
                      </p>
                      <p className="text-white/40 text-xs truncate">{top3[0].ubicacion_tienda || ''}</p>
                      <div className="mt-2 bg-amber-500/20 rounded-lg px-2 py-1 inline-flex items-center gap-1">
                        <Zap className="w-3 h-3 text-amber-400" />
                        <span className="text-amber-400 font-bold text-sm">
                          {top3[0].total_score} pts
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Third place */}
                  {top3[2] ? (
                    <div className={`${PODIUM_COLORS[3].bg} border ${PODIUM_COLORS[3].border} rounded-xl p-4 text-center mt-8`}>
                      <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-amber-600/20 mb-2">
                        <Medal className={`w-5 h-5 ${PODIUM_COLORS[3].icon}`} />
                      </div>
                      <p className={`font-bold text-lg ${PODIUM_COLORS[3].text}`}>3°</p>
                      <p className="text-white font-medium text-sm truncate mt-1">
                        {top3[2].user_id === user?.id ? (
                          <span className="text-green-400">¡Tú!</span>
                        ) : (
                          top3[2].nombre.split(' ')[0]
                        )}
                        {top3[2].training_completed && <span className="ml-1">🎓</span>}
                      </p>
                      <p className="text-white/40 text-xs truncate">{top3[2].ubicacion_tienda || ''}</p>
                      <div className="mt-2 flex justify-center gap-2 text-[10px]">
                        <span className="text-blue-400">{top3[2].total_ventas} V</span>
                        <span className="text-purple-400">{top3[2].total_canjes} C</span>
                      </div>
                    </div>
                  ) : <div />}
                </div>

                {/* Seller list - shows names but NOT clickable for details */}
                {rest.length > 0 && (
                  <div className="space-y-2 mb-6">
                    <h3 className="text-white/60 text-sm font-medium mb-3">Resto del equipo</h3>
                    {rest.map((seller, index) => {
                      const position = index + 4;
                      const isMe = seller.user_id === user?.id;
const score = seller.total_score;
                      
                      return (
                        <div 
                          key={seller.user_id}
                          className={`flex items-center gap-3 p-3 rounded-lg ${
                            isMe ? 'bg-blue-500/20 border border-blue-500/30' : 'bg-white/5 border border-white/5'
                          }`}
                        >
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                            isMe ? 'bg-blue-500/30 text-blue-400' : 'bg-white/10 text-white/60'
                          }`}>
                            {position}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className={`font-medium truncate ${isMe ? 'text-blue-400' : 'text-white'}`}>
                              {isMe ? '¡Tú!' : seller.nombre.split(' ')[0]}
                              {seller.training_completed && <span className="ml-1">🎓</span>}
                            </p>
                            <p className="text-white/40 text-xs truncate">{seller.ubicacion_tienda || ''}</p>
                          </div>
                          <div className="flex items-center gap-1 px-2 py-0.5 bg-amber-500/10 rounded-lg">
                            <Zap className="w-3 h-3 text-amber-400" />
                            <span className="text-amber-400 font-semibold text-sm">{score}</span>
                          </div>
                          <div className="flex gap-2 text-xs">
                            <span className="text-blue-400">{seller.total_ventas}V</span>
                            <span className="text-purple-400">{seller.total_canjes}C</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Full podium for admins */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                  {/* Second place */}
                  {top3[1] ? (
                    <motion.button
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 }}
                      onClick={() => handleSelectSeller(top3[1])}
                      className={`${PODIUM_COLORS[2].bg} border ${PODIUM_COLORS[2].border} rounded-xl p-4 text-center mt-6 hover:scale-105 transition-transform cursor-pointer`}
                    >
                      <div className={`inline-flex items-center justify-center w-10 h-10 rounded-full bg-slate-400/20 mb-2`}>
                        <Medal className={`w-5 h-5 ${PODIUM_COLORS[2].icon}`} />
                      </div>
                      <p className="text-white font-semibold text-sm truncate">
                        {top3[1].nombre.split(' ')[0]}
                        {top3[1].training_completed && (
                          <span className="ml-1 inline-block" title="Entrenamiento completo">🎓</span>
                        )}
                      </p>
                      <p className="text-white/50 text-xs truncate">{top3[1].ubicacion_tienda || 'Sin tienda'}</p>
                      {/* Score badge */}
                      <div className="mt-2 bg-amber-500/20 rounded-lg px-2 py-1 inline-flex items-center gap-1">
                        <Zap className="w-3 h-3 text-amber-400" />
                        <span className="text-amber-400 font-bold text-sm">
                          {top3[1].total_score} pts
                        </span>
                      </div>
                      <div className="mt-2 flex justify-center gap-3 text-[10px]">
                        <span className="text-blue-400">{top3[1].total_ventas} V</span>
                        <span className="text-purple-400">{top3[1].total_canjes} C</span>
                      </div>
                    </motion.button>
                  ) : <div />}

                  {/* First place */}
                  {top3[0] && (
                    <motion.button
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      onClick={() => handleSelectSeller(top3[0])}
                      className={`${PODIUM_COLORS[1].bg} border ${PODIUM_COLORS[1].border} rounded-xl p-4 text-center relative hover:scale-105 transition-transform cursor-pointer`}
                    >
                      <motion.div
                        animate={{ scale: [1, 1.1, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className="absolute -top-3 left-1/2 -translate-x-1/2"
                      >
                        <Award className="w-6 h-6 text-yellow-400" />
                      </motion.div>
                      <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full bg-yellow-500/20 mt-2 mb-2`}>
                        <Trophy className={`w-6 h-6 ${PODIUM_COLORS[1].icon}`} />
                      </div>
                      <p className="text-white font-semibold truncate">
                        {top3[0].nombre.split(' ')[0]}
                        {top3[0].training_completed && (
                          <span className="ml-1 inline-block" title="Entrenamiento completo">🎓</span>
                        )}
                      </p>
                      <p className="text-white/50 text-xs truncate">{top3[0].ubicacion_tienda || 'Sin tienda'}</p>
                      {/* Score badge - prominente */}
                      <div className="mt-3 bg-amber-500/30 rounded-lg px-3 py-1.5 inline-flex items-center gap-1.5">
                        <Zap className="w-4 h-4 text-amber-400" />
                        <span className="text-amber-400 font-bold text-lg">
                          {top3[0].total_score} pts
                        </span>
                      </div>
                      <div className="mt-2 flex justify-center gap-4 text-xs">
                        <span className="text-blue-400">{top3[0].total_ventas} V</span>
                        <span className="text-purple-400">{top3[0].total_canjes} C</span>
                        {top3[0].achievement_points > 0 && (
                          <span className="text-amber-400">🏆{top3[0].achievement_points}</span>
                        )}
                      </div>
                    </motion.button>
                  )}

                  {/* Third place */}
                  {top3[2] ? (
                    <motion.button
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                      onClick={() => handleSelectSeller(top3[2])}
                      className={`${PODIUM_COLORS[3].bg} border ${PODIUM_COLORS[3].border} rounded-xl p-4 text-center mt-8 hover:scale-105 transition-transform cursor-pointer`}
                    >
                      <div className={`inline-flex items-center justify-center w-10 h-10 rounded-full bg-amber-600/20 mb-2`}>
                        <Medal className={`w-5 h-5 ${PODIUM_COLORS[3].icon}`} />
                      </div>
                      <p className="text-white font-semibold text-sm truncate">
                        {top3[2].nombre.split(' ')[0]}
                        {top3[2].training_completed && (
                          <span className="ml-1 inline-block" title="Entrenamiento completo">🎓</span>
                        )}
                      </p>
                      <p className="text-white/50 text-xs truncate">{top3[2].ubicacion_tienda || 'Sin tienda'}</p>
                      {/* Score badge */}
                      <div className="mt-2 bg-amber-500/20 rounded-lg px-2 py-1 inline-flex items-center gap-1">
                        <Zap className="w-3 h-3 text-amber-400" />
                        <span className="text-amber-400 font-bold text-sm">
                          {top3[2].total_score} pts
                        </span>
                      </div>
                      <div className="mt-2 flex justify-center gap-3 text-[10px]">
                        <span className="text-blue-400">{top3[2].total_ventas} V</span>
                        <span className="text-purple-400">{top3[2].total_canjes} C</span>
                      </div>
                    </motion.button>
                  ) : <div />}
                </div>

                {/* Rest of rankings - for admins */}
                {rest.length > 0 && (
                  <div className="space-y-2 mb-6">
                    <h3 className="text-white/60 text-sm font-medium mb-3">Resto del equipo</h3>
                    {rest.map((seller, index) => {
const score = seller.total_score;
                      
                      return (
                        <motion.button
                          key={seller.user_id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.1 * index }}
                          onClick={() => handleSelectSeller(seller)}
                          className="w-full flex items-center gap-3 bg-white/5 rounded-lg p-3 border border-white/5 hover:border-white/20 hover:bg-white/10 transition-all cursor-pointer text-left"
                        >
                          <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/60 font-bold text-sm">
                            {index + 4}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-medium truncate">
                              {seller.nombre}
                              {seller.training_completed && (
                                <span className="ml-1 inline-block" title="Entrenamiento completo">🎓</span>
                              )}
                            </p>
                            <p className="text-white/40 text-xs truncate">{seller.ubicacion_tienda || 'Sin tienda'}</p>
                          </div>
                          {/* Score badge inline */}
                          <div className="flex items-center gap-1 px-2 py-1 bg-amber-500/10 rounded-lg">
                            <Zap className="w-3 h-3 text-amber-400" />
                            <span className="text-amber-400 font-semibold text-sm">{score}</span>
                          </div>
                          <div className="flex gap-3 text-xs">
                            <span className="text-blue-400">{seller.total_ventas}V</span>
                            <span className="text-purple-400">{seller.total_canjes}C</span>
                          </div>
                        </motion.button>
                      );
                    })}
                  </div>
                )}

                {/* Store statistics - for admins only */}
                {storeStats.length > 0 && (
                  <div className="border-t border-white/10 pt-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Store className="w-4 h-4 text-blue-400" />
                      <h3 className="text-white/80 font-medium">Rendimiento por Tienda</h3>
                    </div>
                    <div className="space-y-3">
                      {storeStats.map((store, index) => {
                        const maxVentas = Math.max(...storeStats.map(s => s.ventas), 1);
                        const percentage = (store.ventas / maxVentas) * 100;
                        
                        return (
                          <div key={store.name} className="space-y-1">
                            <div className="flex justify-between text-sm">
                              <span className="text-white">{store.name}</span>
                              <div className="flex gap-3 text-white/60">
                                <span>{store.ventas} ventas</span>
                                <span>₡{store.ahorros.toLocaleString()}</span>
                              </div>
                            </div>
                            <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${percentage}%` }}
                                transition={{ duration: 0.5, delay: 0.1 * index }}
                                className="h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded-full"
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default SellerRankingDashboard;
